import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="md:col-span-5"
        >
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">05 — Say hello</p>
          <h2 className="font-heading font-medium tracking-tight text-3xl md:text-4xl text-neutral-900 mt-4">
            Talk to the humans behind the AI.
          </h2>
          <p className="text-neutral-600 leading-relaxed mt-4">
            Questions about social.5ads.ai, early access to search.5ads.ai, or partnerships —
            drop us a line and we'll reply within a day.
          </p>
        </motion.div>

        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="md:col-span-7 bg-white border border-neutral-200 rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
          data-testid="contact-form"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="text-sm font-medium text-neutral-800">Name</label>
              <input
                name="name"
                value={form.name}
                onChange={onChange}
                placeholder="Your name"
                data-testid="contact-name-input"
                className="mt-2 w-full rounded-xl border border-neutral-300 bg-white px-4 py-3 text-sm outline-none focus:border-neutral-900 focus:ring-2 focus:ring-neutral-900/10 transition-colors"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-800">Email</label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                placeholder="you@company.com"
                data-testid="contact-email-input"
                className="mt-2 w-full rounded-xl border border-neutral-300 bg-white px-4 py-3 text-sm outline-none focus:border-neutral-900 focus:ring-2 focus:ring-neutral-900/10 transition-colors"
              />
            </div>
          </div>
          <div className="mt-5">
            <label className="text-sm font-medium text-neutral-800">Message</label>
            <textarea
              name="message"
              rows={5}
              value={form.message}
              onChange={onChange}
              placeholder="Tell us what you're building…"
              data-testid="contact-message-input"
              className="mt-2 w-full rounded-xl border border-neutral-300 bg-white px-4 py-3 text-sm outline-none focus:border-neutral-900 focus:ring-2 focus:ring-neutral-900/10 transition-colors resize-none"
            />
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit-button"
            className="mt-6 inline-flex items-center gap-2 bg-neutral-900 text-white text-sm font-medium px-6 py-3.5 rounded-full hover:bg-[#FF4D00] transition-colors duration-300 disabled:opacity-60"
          >
            {sending ? "Sending…" : "Send message"} <Send size={15} />
          </button>
        </motion.form>
      </div>
    </section>
  );
}
